import {
  DEFAULT_DISPLAY,
  FRACTIONS,
  isLengthFormat,
  isUnit,
  MAX_PRECISION,
  type LengthDisplay,
  type LengthFormat,
  type Unit,
} from '../model';
import { DEFAULT_VIEW_MARGIN } from '../views';

/** Settings for a whole drawing, set in the Document section and stored with it. */
export interface ScriptSettings {
  /** How far each view's frame stands clear of what it holds, in drawing units. */
  readonly viewMargin: number;
  /** What every number in the drawing means; changing it changes no number. */
  readonly unit: Unit;
  /** How lengths are shown. */
  readonly format: LengthFormat;
  /** Decimal places a decimal format shows. */
  readonly precision: number;
  /** The smallest fraction a fractional format shows, as a denominator. */
  readonly fraction: number;
  /** Whether dimension text ends in its unit. */
  readonly dimensionUnits: boolean;
}

export const DEFAULT_SETTINGS: ScriptSettings = {
  viewMargin: DEFAULT_VIEW_MARGIN,
  unit: DEFAULT_DISPLAY.unit,
  format: DEFAULT_DISPLAY.format,
  precision: DEFAULT_DISPLAY.precision,
  fraction: DEFAULT_DISPLAY.fraction,
  dimensionUnits: DEFAULT_DISPLAY.suffix,
};

const MAX_VIEW_MARGIN = 1e6;

/** Why `margin` cannot be a view margin, or null when it can. */
export function viewMarginProblem(margin: number): string | null {
  if (!Number.isFinite(margin) || margin < 0 || margin > MAX_VIEW_MARGIN)
    return `A view margin must be a number from 0 to ${MAX_VIEW_MARGIN}`;
  return null;
}

/** Why `precision` cannot be a precision, or null when it can. */
export function precisionProblem(precision: number): string | null {
  if (!Number.isInteger(precision) || precision < 0 || precision > MAX_PRECISION)
    return `A precision must be a whole number from 0 to ${MAX_PRECISION}`;
  return null;
}

/** Why `fraction` cannot be a smallest fraction, or null when it can. */
export function fractionProblem(fraction: number): string | null {
  if (!(FRACTIONS as readonly number[]).includes(fraction))
    return `A fraction must be one of ${FRACTIONS.join(', ')}`;
  return null;
}

/** How dimension text shows lengths; panels always show the unit. */
export const displayOf = (settings: ScriptSettings): LengthDisplay => ({
  unit: settings.unit,
  format: settings.format,
  precision: settings.precision,
  fraction: settings.fraction,
  suffix: settings.dimensionUnits,
});

/** Keeps only well-formed settings from whatever a client sent, defaulting the rest. */
export function cleanSettings(value: unknown): ScriptSettings {
  const { viewMargin, unit, format, precision, fraction, dimensionUnits } = (
    typeof value === 'object' && value !== null ? value : {}
  ) as Record<string, unknown>;
  const valid = (each: unknown, problem: (n: number) => string | null) =>
    typeof each === 'number' && problem(each) === null;
  return {
    viewMargin: valid(viewMargin, viewMarginProblem)
      ? (viewMargin as number)
      : DEFAULT_SETTINGS.viewMargin,
    unit: isUnit(unit) ? unit : DEFAULT_SETTINGS.unit,
    format: isLengthFormat(format) ? format : DEFAULT_SETTINGS.format,
    precision: valid(precision, precisionProblem)
      ? (precision as number)
      : DEFAULT_SETTINGS.precision,
    fraction: valid(fraction, fractionProblem) ? (fraction as number) : DEFAULT_SETTINGS.fraction,
    dimensionUnits:
      typeof dimensionUnits === 'boolean' ? dimensionUnits : DEFAULT_SETTINGS.dimensionUnits,
  };
}

export const sameSettings = (a: ScriptSettings, b: ScriptSettings): boolean =>
  a.viewMargin === b.viewMargin &&
  a.unit === b.unit &&
  a.format === b.format &&
  a.precision === b.precision &&
  a.fraction === b.fraction &&
  a.dimensionUnits === b.dimensionUnits;
